import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { BudgetExhaustedModal } from '@/components/budget'
import type { BudgetExceededDetails } from '@/types/budget'

interface BudgetContextValue {
  /** Show the budget exhausted modal with details */
  showBudgetExhausted: (details: BudgetExceededDetails) => void
  /** Hide the budget exhausted modal */
  hideBudgetExhausted: () => void
  /** Whether the modal is currently shown */
  isExhausted: boolean
}

const BudgetContext = createContext<BudgetContextValue | null>(null)

export interface BudgetProviderProps {
  children: ReactNode
}

/**
 * Provider for global budget exhausted modal.
 * Wrap your app with this to enable 402 error handling.
 * 
 * @example
 * ```tsx
 * <BudgetProvider>
 *   <App />
 * </BudgetProvider>
 * ```
 */
export function BudgetProvider({ children }: BudgetProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [details, setDetails] = useState<BudgetExceededDetails | undefined>()

  const showBudgetExhausted = useCallback((newDetails: BudgetExceededDetails) => {
    setDetails(newDetails)
    setIsOpen(true)
  }, [])

  const hideBudgetExhausted = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <BudgetContext.Provider
      value={{
        showBudgetExhausted,
        hideBudgetExhausted,
        isExhausted: isOpen,
      }}
    >
      {children}
      <BudgetExhaustedModal
        open={isOpen}
        onOpenChange={setIsOpen}
        details={details}
      />
    </BudgetContext.Provider>
  )
}

/**
 * Hook to access budget context for showing/hiding exhausted modal.
 * 
 * @example
 * ```tsx
 * const { showBudgetExhausted } = useBudgetContext()
 * 
 * // In error handler:
 * if (error.status === 402) {
 *   showBudgetExhausted(error.body.details)
 * }
 * ```
 */
export function useBudgetContext() {
  const context = useContext(BudgetContext)
  if (!context) {
    throw new Error('useBudgetContext must be used within a BudgetProvider')
  }
  return context
}

/**
 * Check if an API error is a budget exceeded error (HTTP 402).
 * Returns the details if it is, otherwise null.
 */
export function isBudgetExceededError(error: unknown): BudgetExceededDetails | null {
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as { status: number }).status === 402 &&
    'body' in error
  ) {
    const body = (error as { body: unknown }).body
    if (
      body &&
      typeof body === 'object' &&
      'details' in body
    ) {
      return (body as { details: BudgetExceededDetails }).details
    }
  }
  return null
}
